/**
 * THE DESIGNED CARD — one canonical object, one source of truth.
 *
 * Every funnel screen that shows the customer's card (S5 design, S6 delivery,
 * S7 confirmation, S8 order) reads THIS object and renders it through the ONE
 * renderer (<DesignedCardFace/>). Screens must not re-derive colour, avatar or
 * framing from their own defaults — that drift is exactly what made the
 * confirmation card lose its background and the delivery card show the wrong
 * colour.
 *
 * Written at design time (S5) and amended by later screens only when the user
 * changes a card property there (e.g. S6's colour swatches).
 */
import { toCssColour } from "@/lib/color";
import {
  readCardCustomization,
  readDetails,
  readSelectedVariant,
} from "@/lib/consumer-session";

export interface DesignedCard {
  /** The generation this design is built on. */
  generationId?: string | null;
  /** The CUTOUT url (never a raw square) — see cutoutOrOriginal. */
  avatarUrl: string | null;
  /** Background colour the user picked (hex/hsl string). */
  colour: string;
  /** Human name of the colour, for copy and share captions. */
  cname?: string;
  orientation: "portrait" | "landscape";
  /** Avatar zoom, percent. 100 = untouched. */
  avatarZoom: number;
  /** Avatar framing in percent; 50/50 = centred. */
  avatarPosition: { x: number; y: number };
  variantNumber?: number | null;
}

const KEY = (slug: string) => `cmoji.consumer.card.${slug}`;

export const DEFAULT_DESIGNED_CARD: DesignedCard = {
  avatarUrl: null,
  colour: "#111111",
  orientation: "portrait",
  avatarZoom: 100,
  avatarPosition: { x: 50, y: 50 },
};

function coerce(raw: any): DesignedCard | null {
  if (!raw || typeof raw !== "object") return null;
  const pos = raw.avatarPosition ?? {};
  return {
    generationId: raw.generationId ?? null,
    avatarUrl: typeof raw.avatarUrl === "string" ? raw.avatarUrl : null,
    colour: typeof raw.colour === "string" && raw.colour ? raw.colour : DEFAULT_DESIGNED_CARD.colour,
    cname: typeof raw.cname === "string" ? raw.cname : undefined,
    orientation: raw.orientation === "landscape" ? "landscape" : "portrait",
    avatarZoom: Number.isFinite(+raw.avatarZoom) ? Math.min(200, Math.max(50, +raw.avatarZoom)) : 100,
    avatarPosition: {
      x: Number.isFinite(+pos.x) ? +pos.x : 50,
      y: Number.isFinite(+pos.y) ? +pos.y : 50,
    },
    variantNumber: raw.variantNumber ?? null,
  };
}

export function saveDesignedCard(slug: string, card: DesignedCard) {
  try {
    localStorage.setItem(KEY(slug), JSON.stringify(card));
  } catch {
    /* private mode — the in-memory object still drives this screen */
  }
}

/** Drop the staged design so the next order starts from its own pick. */
export function clearDesignedCard(slug: string) {
  try {
    localStorage.removeItem(KEY(slug));
    localStorage.removeItem(`cmoji.consumer.variant.${slug}`);
  } catch {
    /* storage unavailable */
  }
}

/** Merge a partial change (e.g. S6 colour swatch) into the canonical object. */
export function patchDesignedCard(slug: string, patch: Partial<DesignedCard>): DesignedCard {
  const next = { ...readDesignedCard(slug), ...patch };
  saveDesignedCard(slug, next);
  return next;
}

/**
 * Read the canonical card. Falls back to the pre-existing staged keys so a
 * session that was mid-funnel when this shipped still renders its real design
 * instead of a programme default.
 */
export function readDesignedCard(slug: string): DesignedCard {
  try {
    const stored = coerce(JSON.parse(localStorage.getItem(KEY(slug)) || "null"));
    if (stored) return stored;
  } catch {
    /* fall through to legacy reconstruction */
  }
  const variant = readSelectedVariant(slug);
  const custom = readCardCustomization(slug);
  const details = readDetails(slug);
  return {
    generationId: variant?.generation_id ?? null,
    avatarUrl: variant?.image_url ?? null,
    /* Stored rows keep bare HSL components; unwrapped they paint nothing. */
    colour:
      toCssColour(details?.card_color) ||
      toCssColour(variant?.card_color) ||
      custom?.colour ||
      DEFAULT_DESIGNED_CARD.colour,
    cname: custom?.cname,
    orientation:
      (details?.orientation as any) ||
      (variant?.orientation as any) ||
      custom?.orientation ||
      "portrait",
    avatarZoom: variant?.avatar_scale ? Math.round(variant.avatar_scale * 100) : custom?.size ?? 100,
    avatarPosition: variant?.avatar_position ?? {
      x: custom?.pos_x ?? 50,
      y: custom?.pos_y ?? 50,
    },
    variantNumber: variant?.variant_number ?? null,
  };
}
